import { Component, OnInit, ViewChild, ViewChildren, QueryList, ElementRef, Input, Output, EventEmitter, OnDestroy, AfterViewInit, NgZone, HostListener } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { URLS } from 'src/app/constants/URLS';
import { TVNavigationService, Region } from 'src/app/_service/tv-navigation.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';

/**
 * CustomCarouselComponent
 *
 * NOTE: This component does NOT handle arrow key navigation.
 * On TV platforms, all D-pad navigation is handled by TVNavigationService.
 * Do NOT add @HostListener handlers for ArrowLeft, ArrowRight, ArrowUp, ArrowDown
 * as they would conflict with the centralized TV navigation system.
 *
 * Touch/mouse drag is still handled here for mobile/desktop web.
 */
@Component({
  selector: 'app-custom-carousel',
  templateUrl: './custom-carousel.component.html',
  styleUrls: ['./custom-carousel.component.css']
})
export class CustomCarouselComponent implements OnInit, OnDestroy, AfterViewInit {
  
  @Input() items: any[] = [];
  @Input() customCarouselOptions?: any;
  @Input() type: string = '';
  @Input() rowId: string = '';

  @Output() itemClick = new EventEmitter<any>();
  @Output() tvFocusedChannel= new EventEmitter<any>();

  @ViewChild('owlCarousel', { static: false }) owlCarousel: ElementRef;
  @ViewChild('carouselTrack', { static: false }) carouselTrack: ElementRef;
  @ViewChildren('card') cardRefs: QueryList<ElementRef>;
  
  currentIndex = 0;
  translateX = 0;
  isAnimating = false;
  itemWidth = 0;
  itemsPerView = 1;
  maxIndex = 0;
  showNavigation = true;
  marginRight = 0;
  containerWidth = 0;
  isCarouselEnabled = true;

  isDragging = false;
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;
  private lastX = 0;
  private lastY = 0;
  private initialTranslateX = 0;
  private startTime = 0;
  private lastTime = 0;
  private velocityX = 0;
  private velocityY = 0;
  private isHorizontalSwipe = false;
  private isVerticalSwipe = false;
  private swipeDecided = false;
  private touchStarted = false;
  private moved = false;
  private minSwipeDistance = 10;
  private snapThreshold = 0.3;
  private resizeObserver?: ResizeObserver;
  private animationFrameId: number = 0;

  // TV Navigation integration
  private destroy$ = new Subject<void>();
  private generatedRegionId: string = '';
  isTvFocused = false;
  focusedCardIndex = -1;

  constructor(
    private ngZone: NgZone,
    private tvNavService: TVNavigationService,
    private deviceService: DeviceService
  ) { }

  ngOnInit(): void {
    this.mergeOptions();
    this.updateCarouselView();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.setupCarousel();
    });

    // TV Navigation integration
    if (this.deviceService.isTV()) {
      // Generate unique regionId if not provided
      this.generatedRegionId = this.rowId || `carousel-${this.type}`;

      // Wait for cards to render
      setTimeout(() => {
        this.registerWithNavigation();
      }, 100);

      // Re-register when cards change (items added/removed)
      this.cardRefs.changes
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.registerWithNavigation();
        });

      // Subscribe to focus changes to update tv-focus styling and scroll carousel
      this.tvNavService.focus$
        .pipe(takeUntil(this.destroy$))
        .subscribe(focusKey => {
          if (focusKey && focusKey.startsWith(this.generatedRegionId + ':')) {
            this.isTvFocused = true;
            this.focusedCardIndex = parseInt(focusKey.split(':')[1], 10);
            // Scroll this carousel's track so the focused card is visible
            this.scrollToFocusedCard(this.focusedCardIndex);
            if(this.focusedCardIndex >=0 && this.focusedCardIndex < this.items.length){
              this.tvFocusedChannel.emit(this.items[this.focusedCardIndex]);
            }
          } else {
            this.isTvFocused = false;
            this.focusedCardIndex = -1;
          }
        });
    }
  }

  ngOnDestroy(): void {
    // TV Navigation cleanup
    this.destroy$.next();
    this.destroy$.complete();

    if (this.deviceService.isTV() && this.generatedRegionId) {
      this.tvNavService.unregisterRegion(this.generatedRegionId);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.removeEventListeners();
  }

  /**
   * Register this carousel row with TVNavigationService
   */
  private registerWithNavigation(): void {
    if (this.cardRefs.length === 0) return;

    const region: Region = {
      id: this.generatedRegionId,
      type: 'row',
      elementRef: this.owlCarousel,
      focusableItems: this.cardRefs.toArray()
    };

    this.tvNavService.registerRegion(region);
  }

  private mergeOptions(): void {
    if (this.customCarouselOptions) {
      this.customOptions = { ...this.customOptions, ...this.customCarouselOptions };
    }
  }

  private setupCarousel(): void {
    if (this.isCarouselEnabled) {
      this.calculateDimensions();
      this.setupResizeObserver();
      this.setupEventListeners();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateCarouselView();
    requestAnimationFrame(() => {
      this.calculateDimensions();
    });
  }

  updateCarouselView() {
    const screenWidth = window.innerWidth;
    const minItemsForCurrentScreen = this.getMinItemsForScreen(screenWidth);
    this.isCarouselEnabled = true;
    this.updateResponsiveSettings();
    if (this.items.length <= minItemsForCurrentScreen) {
      this.showNavigation = false;
      this.customOptions.mouseDrag = false;
      this.customOptions.touchDrag = false;
    }
    requestAnimationFrame(() => {
      this.calculateDimensions();
    });
  }

  private updateResponsiveSettings() {
    const screenWidth = window.innerWidth;
    const responsiveConfig = this.getResponsiveConfigForScreen(screenWidth);
    
    this.itemsPerView = responsiveConfig.items;
    this.marginRight = responsiveConfig.margin || 0;
    this.showNavigation = responsiveConfig.nav !== false && this.customOptions.nav !== false;
  }

  private getResponsiveConfigForScreen(screenWidth: number) {
    const breakpoints = Object.keys(this.customOptions.responsive)
      .map(key => parseInt(key))
      .sort((a, b) => b - a);
    
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.customOptions.responsive[breakpoint];
      }
    }
    
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.customOptions.responsive[smallestBreakpoint];
  }

  private calculateDimensions() {
    if (!this.owlCarousel || !this.isCarouselEnabled) return;

    this.containerWidth = this.owlCarousel.nativeElement.offsetWidth;
    const totalItems = this.items.length;
    
    if (totalItems === 0) return;

    const totalMarginSpace = this.marginRight * (this.itemsPerView - 1);
    const availableSpace = this.containerWidth - totalMarginSpace;
    
    this.itemWidth = Math.floor(availableSpace / this.itemsPerView);
    
    if (this.itemWidth < 50) {
      this.itemWidth = Math.floor(this.containerWidth / this.itemsPerView);
    }
    
    this.maxIndex = Math.max(0, totalItems - this.itemsPerView);

    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
    
    this.updateTransform();
  }

  private updateTransform() {
    const newTranslateX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.translateX = newTranslateX;
  }

  /**
   * TV: Hotstar-style — always keep the focused card as the first visible card.
   * When pressing RIGHT, the carousel slides left so the new card is at position 0.
   */
  private scrollToFocusedCard(cardIndex: number) {
    if (!this.isCarouselEnabled || this.itemWidth === 0) return;

    // Always position the focused card as the first visible (leftmost) card
    this.currentIndex = Math.min(cardIndex, this.maxIndex);
    this.isAnimating = true;
    this.updateTransform();
    setTimeout(() => this.isAnimating = false, 350);
  }

  private setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined' && this.owlCarousel) {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          if (this.isCarouselEnabled) {
            this.calculateDimensions();
          }
        });
      });
      this.resizeObserver.observe(this.owlCarousel.nativeElement);
    }
  }

  private setupEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      
      element.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
      element.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      element.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
      element.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false });
      
      element.addEventListener('mousedown', this.onMouseDown.bind(this), { passive: false });
      document.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: false });
      document.addEventListener('mouseup', this.onMouseUp.bind(this), { passive: false });
    }
  }

  private removeEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      
      element.removeEventListener('touchstart', this.onTouchStart.bind(this));
      element.removeEventListener('touchmove', this.onTouchMove.bind(this));
      element.removeEventListener('touchend', this.onTouchEnd.bind(this));
      element.removeEventListener('touchcancel', this.onTouchEnd.bind(this));
      
      element.removeEventListener('mousedown', this.onMouseDown.bind(this));
      document.removeEventListener('mousemove', this.onMouseMove.bind(this));
      document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex && !this.isAnimating) {
      this.currentIndex++;
      this.smoothSlide();
    }
  }

  prev() {
    if (this.currentIndex > 0 && !this.isAnimating) {
      this.currentIndex--;
      this.smoothSlide();
    }
  }

  private smoothSlide() {
    this.isAnimating = true;
    this.updateTransform();
    
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  onTouchStart(event: TouchEvent) {
    if (!this.customOptions.touchDrag || !this.isCarouselEnabled) return;
    
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.touchStarted = true;
      this.moved = false;
      this.swipeDecided = false;
      this.isHorizontalSwipe = false;
      this.isVerticalSwipe = false;
      
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.currentX = touch.clientX;
      this.currentY = touch.clientY;
      this.lastX = touch.clientX;
      this.lastY = touch.clientY;
      this.initialTranslateX = this.translateX;
      this.startTime = Date.now();
      this.lastTime = this.startTime;
      this.velocityX = 0;
      this.velocityY = 0;
      
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = 0;
      }
    }
  }

  onTouchMove(event: TouchEvent) {
    if (!this.touchStarted || event.touches.length !== 1) return;
    
    const touch = event.touches[0];
    const now = Date.now();
    
    this.currentX = touch.clientX;
    this.currentY = touch.clientY;
    
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    
    if (!this.swipeDecided && (Math.abs(deltaX) > this.minSwipeDistance || Math.abs(deltaY) > this.minSwipeDistance)) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      this.isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);
      this.swipeDecided = true;
    }
    
    if (this.swipeDecided && this.isHorizontalSwipe) {
      this.moved = true;
      this.isDragging = true;
      
      if (now - this.lastTime > 16) {
        this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
        this.lastX = this.currentX;
        this.lastTime = now;
      }
      
      this.updateCarouselPosition(deltaX);
      
      event.preventDefault();
      event.stopPropagation();
    }
    else if (this.swipeDecided && this.isVerticalSwipe) {
      return;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.touchStarted) return;
    
    this.touchStarted = false;
    
    if (this.isDragging && this.moved) {
      this.endDrag();
      event.preventDefault();
      event.stopPropagation();
    }
    
    this.resetDragState();
  }

  onMouseDown(event: MouseEvent) {
    if (!this.customOptions.mouseDrag || !this.isCarouselEnabled) return;
    
    this.isDragging = true;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.lastX = event.clientX;
    this.initialTranslateX = this.translateX;
    this.startTime = Date.now();
    this.lastTime = this.startTime;
    this.velocityX = 0;
    
    event.preventDefault();
    event.stopPropagation();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    
    const now = Date.now();
    this.currentX = event.clientX;
    
    if (now - this.lastTime > 16) {
      this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
      this.lastX = this.currentX;
      this.lastTime = now;
    }
    
    const deltaX = this.currentX - this.startX;
    this.updateCarouselPosition(deltaX);
    
    event.preventDefault();
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isDragging) return;
    
    this.endDrag();
    this.resetDragState();
  }

  private updateCarouselPosition(deltaX: number) {
    let newTranslateX = this.initialTranslateX + deltaX;
    
    const maxTranslateX = 0;
    const minTranslateX = -((this.itemWidth + this.marginRight) * this.maxIndex);
    
    if (newTranslateX > maxTranslateX) {
      newTranslateX = maxTranslateX + (newTranslateX - maxTranslateX) * 0.3;
    } else if (newTranslateX < minTranslateX) {
      newTranslateX = minTranslateX + (newTranslateX - minTranslateX) * 0.3;
    }
    
    this.ngZone.runOutsideAngular(() => {
      this.translateX = newTranslateX;
    });
  }

  private endDrag() {
    this.isDragging = false;
    
    const deltaX = this.currentX - this.startX;
    const duration = Date.now() - this.startTime;
    const itemSize = this.itemWidth + this.marginRight;
    
    const currentPosition = -this.translateX / itemSize;
    
    if (Math.abs(this.velocityX) > 1.0 && duration < 300) {
      const velocityFactor = Math.abs(this.velocityX) * 2;
      const itemsToMove = Math.min(Math.ceil(velocityFactor), 3);
      
      if (this.velocityX > 0) {
        this.currentIndex = Math.max(0, Math.floor(currentPosition) - itemsToMove + 1);
      } else {
        this.currentIndex = Math.min(this.maxIndex, Math.floor(currentPosition) + itemsToMove);
      }
    } else {
      this.currentIndex = Math.round(currentPosition);
    }
    
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));
    
    this.animateToPosition();
  }

  private animateToPosition() {
    const targetX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.isAnimating = true;
    this.translateX = targetX;
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  private resetDragState() {
    this.isDragging = false;
    this.touchStarted = false;
    this.moved = false;
    this.swipeDecided = false;
    this.isHorizontalSwipe = false;
    this.isVerticalSwipe = false;
    this.velocityX = 0;
    this.velocityY = 0;
  }

  getMinItemsForScreen(screenWidth: number): number {
    const breakpoints = Object.keys(this.customOptions.responsive).map(key => parseInt(key)).sort((a, b) => b - a); 
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.customOptions.responsive[breakpoint].items;
      }
    }
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.customOptions.responsive[smallestBreakpoint].items;
  }

  onItemClick(item: any): void {
    if (!this.isDragging) {
      this.itemClick.emit(item);
    }
  }

  customOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 600,
    nav: true,
    navText: ['<i class="fa fa-chevron-left"></i>', '<i class="fa fa-chevron-right"></i>'],
    margin: 15,
    autoplay: false,
    responsive: {
      0: {
        items: 2.8,
        nav: false,
        margin: 6
      },
      400: {
        items: 3.2,
        nav: false,
        margin: 6
      },
      600: {
        items: 3.5,
        nav: false,
        margin: 3
      },
      800: {
        items: 4.2,
        nav: true,
        margin: 8
      },
      1000: {
        items: 5.2,
        nav: true,
        margin: 10
      },
      1200: {
        items: 5.2,
        nav: true,
        margin: 10
      },
      1600: {
        items: 6.2,
        nav: true,
        margin: 10
      }
    }
  };
}